import React, { useRef, useState } from 'react';

interface Item {
  q: string;
  a: string;
}

const ITEMS: Item[] = [
  {
    q: 'How is Niyyah different from other Islamic apps?',
    a: "Most Islamic apps are reference tools you look something up, then close them. Niyyah is built around a daily five-minute practice: scholar-reviewed lessons on real-life topics, a prayer companion, and habit-building rooted in the Quran and Sunnah. It's designed to change how you live your deen, not just what you look up."
  },
  {
    q: 'Who reviews the content?',
    a: 'Every lesson on Niyyah is reviewed by scholars and experts before it reaches you. AI helps us produce content at speed; the experts ensure every word is faithful to the sources and useful in real life.'
  },
  {
    q: 'Which madhab does Niyyah follow?',
    a: "Niyyah serves Muslims across all four major madhabs - Hanafi, Maliki, Shafi'i, and Hanbali. Where rulings differ between schools, we surface the relevant positions clearly. You're never locked into one perspective."
  },
  {
    q: "I'm a revert or new to Islam - is this for me?",
    a: "Yes. Niyyah was built with reverts and beginners in mind. No assumed prior knowledge. No judgment. No gatekeeping. Start exactly where you are. The 'New to Islam' topic path is a great first stop."
  },
  {
    q: "I'm a busy professional with no time. Does five minutes really do anything?",
    a: "Five minutes a day, every day, is the practice. The app is built around the reality that you're not going to find a free hour - and you don't need to. Consistent small steps, rooted in the Quran and reviewed by a scholar, are how a daily relationship with your deen is actually built."
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes, always. Cancel directly through your App Store or Google Play subscription settings at any time. If you cancel, your paid access continues until the end of the current billing period. No emails, no friction.'
  }
];

const FaqItem: React.FC<{ item: Item; open: boolean; onToggle: () => void }> = ({ item, open, onToggle }) => {
  const answerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const maxHeight = open && innerRef.current ? innerRef.current.scrollHeight : 0;

  return (
    <div className={`faq-item${open ? ' open' : ''}`} role="listitem">
      <button className="faq-question" aria-expanded={open} onClick={onToggle} type="button">
        {item.q}{' '}
        <span className="faq-icon" aria-hidden="true">
          +
        </span>
      </button>
      <div className="faq-answer" ref={answerRef} style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}>
        <div className="faq-answer-inner" ref={innerRef}>
          {item.a}
        </div>
      </div>
    </div>
  );
};

const Faq: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section id="faq">
      <div className="faq-inner">
        <div className="section-header reveal">
          <p className="eyebrow">Questions</p>
          <h2 className="h2-display">
            Everything <em>worth asking.</em>
          </h2>
        </div>
        <div className="faq-list" role="list">
          {ITEMS.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Faq;
